/*
  ClinicalSummaryCard — structured clinical overview at the top of a
  client record. Five collapsible sections following the Living
  Energy spec:

      * Presenting problem
      * History
      * Medications
      * Precautions
      * Goals

  Behaviour:
    - Loads existing summary on mount (backwards compatible — legacy
      free-text summaries render into "History" as a fallback).
    - Debounced autosave (2s after typing stops) OR immediate save on
      blur — sections are saved as a partial payload so one section
      never overwrites another.
    - Shows saving/saved indicator + last-reviewed by/when.
    - "History" popover lists prior full-summary snapshots (newest
      first, all 5 sections + text).
    - "Mark reviewed" button forces a stamp without editing
      (audit-safe).
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ClipboardCheck, History, Check, Loader2, ShieldCheck, ChevronRight, ChevronDown, Sparkles,
} from "lucide-react";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";
import AiSourcesToggle from "@/components/AiSourcesToggle";
import { useCanonicalClient } from "@/lib/canonicalClient";

const AUTOSAVE_DELAY_MS = 2000;

const SECTIONS = [
  { key: "presenting_problem", label: "Presenting problem",
    placeholder: "Why the client is here now — main concern in their words + your clinical framing." },
  { key: "history",             label: "History",
    placeholder: "Relevant medical, surgical, occupational, exercise history." },
  { key: "medications",         label: "Medications",
    placeholder: "Current medications + supplements. Note anything that affects exercise (BP meds, anticoagulants, bisphosphonates…)." },
  { key: "precautions",         label: "Precautions",
    placeholder: "Absolute + relative exercise restrictions, red-flag movements to avoid, monitoring needs." },
  { key: "goals",               label: "Goals",
    placeholder: "Concrete client-agreed goals. Time-bound where possible." },
];

const EMPTY_SECTIONS = SECTIONS.reduce((a, s) => { a[s.key] = ""; return a; }, {});

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                   : d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}


export default function ClinicalSummaryCard({ email }) {
  // Phase-2 canonical scope — when the parent page is on
  // ``/clients/<email>?as=<cid>`` we send ``?canonical_client_id=<cid>``
  // so the summary loads from + writes to the per-canonical document
  // (each shared-email sibling gets an isolated summary).
  const canonical = useCanonicalClient();
  const canonicalId = canonical?.clientId || "";
  const [sections, setSections] = useState({ ...EMPTY_SECTIONS });
  const [initial, setInitial] = useState({ ...EMPTY_SECTIONS });
  const [aiProvenance, setAiProvenance] = useState({});
  const [reviewedBy, setReviewedBy] = useState(null);
  const [reviewedAt, setReviewedAt] = useState(null);
  const [revisionsCount, setRevisionsCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Collapsed state per section — presenting problem + goals expanded
  // by default (the two the practitioner reads first at the start of
  // every consult).
  const [collapsed, setCollapsed] = useState({
    presenting_problem: false, history: true, medications: true,
    precautions: false, goals: false,
  });
  const autosaveTimer = useRef(null);

  const encodedEmail = encodeURIComponent(email || "");

  const applyServer = useCallback((data) => {
    // Merge in the legacy free-text `text` under history so clients
    // that pre-date the structured schema keep their content visible.
    const serverSections = data?.sections || {};
    const legacyText = (data?.text || "").trim();
    const merged = { ...EMPTY_SECTIONS };
    for (const s of SECTIONS) merged[s.key] = serverSections[s.key] || "";
    if (legacyText && !merged.history) merged.history = legacyText;
    setSections(merged);
    setInitial(merged);
    setAiProvenance(data?.ai_provenance || {});
    setReviewedBy(data?.last_reviewed_by || null);
    setReviewedAt(data?.last_reviewed_at || null);
    setRevisionsCount(data?.revisions_count || 0);
  }, []);

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${encodedEmail}/clinical-summary`, {
        params: canonicalId ? { canonical_client_id: canonicalId } : undefined,
      })
      .then((r) => { if (!cancelled) applyServer(r.data); })
      .catch(() => { /* fine — empty state */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email, encodedEmail, applyServer, canonicalId]);

  const doSave = useCallback(async (nextSections) => {
    if (!email) return;
    setSaving(true);
    try {
      const r = await api.put(`/clients/${encodedEmail}/clinical-summary`,
        { sections: nextSections },
        { params: canonicalId ? { canonical_client_id: canonicalId } : undefined },
      );
      applyServer(r.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      toast.error("Couldn't save summary", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }, [email, encodedEmail, applyServer, canonicalId]);

  // Debounced autosave — kicks off 2s after the last keystroke.
  useEffect(() => {
    if (loading) return;
    const dirty = SECTIONS.some((s) => sections[s.key] !== initial[s.key]);
    if (!dirty) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { doSave(sections); }, AUTOSAVE_DELAY_MS);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [sections, initial, loading, doSave]);

  function handleBlur() {
    const dirty = SECTIONS.some((s) => sections[s.key] !== initial[s.key]);
    if (dirty) {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      doSave(sections);
    }
  }
  async function handleMarkReviewed() {
    await doSave(sections);
    toast.success("Marked as reviewed");
  }
  async function draftWithAi(mode = "fill_empty") {
    if (!email) return;
    // Flush any pending edits first so the AI doesn't stomp them.
    if (SECTIONS.some((s) => sections[s.key] !== initial[s.key])) {
      await doSave(sections);
    }
    setDrafting(true);
    try {
      const r = await api.post(
        `/clients/${encodedEmail}/clinical-summary/ai-draft`,
        { mode },
        { params: canonicalId ? { canonical_client_id: canonicalId } : undefined },
      );
      applyServer(r.data);
      const result = r.data?.ai_draft_result || {};
      const written = result.written || [];
      const skipped = result.skipped || [];
      if (result.reason === "no_writable_sections") {
        toast.info("Nothing to draft", {
          description: "Every section already has content. Use 'Replace AI drafts' to refresh AI-authored sections.",
          duration: 5500,
        });
      } else if (written.length === 0) {
        toast.info("The AI had nothing new to add", {
          description: "The record didn't support extra content for the remaining sections.",
        });
      } else {
        toast.success(`Drafted ${written.length} section${written.length === 1 ? "" : "s"}`, {
          description: `Wrote: ${written.join(", ")}${skipped.length ? ` — kept clinician content in: ${skipped.join(", ")}` : ""}`,
          duration: 6000,
        });
      }
    } catch (e) {
      toast.error("AI draft failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setDrafting(false); }
  }
  function setSection(key, value) {
    setSections((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Card className="border-zinc-200 shadow-sm mb-4" data-testid="clinical-summary-card">
      <div className="p-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ShieldCheck className="h-4 w-4 text-indigo-700 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-zinc-950 tracking-tight">Clinical Summary</h3>
          <span className="text-2xs text-zinc-400 hidden md:inline">
            Presenting problem · History · Medications · Precautions · Goals
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-zinc-500">
          {saving && (
            <span className="inline-flex items-center gap-1 text-zinc-500" data-testid="cs-saving">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          )}
          {!saving && savedFlash && (
            <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="cs-saved">
              <Check className="h-3 w-3" /> Saved
            </span>
          )}
          {!saving && !savedFlash && reviewedAt && (
            <span data-testid="cs-last-reviewed">
              Reviewed by <b className="text-zinc-700">{reviewedBy?.name || "—"}</b> · {fmtWhen(reviewedAt)}
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-2">
        {SECTIONS.map((s) => {
          const prov = aiProvenance?.[s.key];
          return (
          <div key={s.key} className="rounded border border-zinc-100"
                data-testid={`cs-section-${s.key}`}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] }))}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-zinc-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-inset"
              data-testid={`cs-toggle-${s.key}`}
              aria-expanded={!collapsed[s.key]}
            >
              {collapsed[s.key]
                ? <ChevronRight className="h-3.5 w-3.5 text-zinc-500" />
                : <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />}
              <span className="text-xs font-semibold text-zinc-800">{s.label}</span>
              {sections[s.key]?.trim() && (
                <span className="text-2xs text-zinc-500 ml-1">
                  {sections[s.key].length} chars
                </span>
              )}
              {prov && (
                <AIProvenanceBadge
                  confidence={prov.confidence}
                  reviewedBy={reviewedBy?.name}
                  reviewedAt={reviewedAt}
                  size="inline"
                  className="ml-1"
                  testId={`cs-provenance-${s.key}`}
                />
              )}
            </button>
            {!collapsed[s.key] && (
              <div className="px-2 pb-2">
                <Textarea
                  value={sections[s.key] || ""}
                  onChange={(e) => setSection(s.key, e.target.value)}
                  onBlur={handleBlur}
                  placeholder={s.placeholder}
                  rows={3}
                  disabled={loading}
                  className="text-sm resize-y font-normal leading-relaxed"
                  data-testid={`cs-input-${s.key}`}
                  aria-label={s.label}
                />
                {prov?.sources_used?.length > 0 && (
                  <AiSourcesToggle
                    sources={prov.sources_used}
                    testId={`cs-sources-${s.key}`}
                  />
                )}
              </div>
            )}
          </div>
        );
        })}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Button
            size="sm" variant="outline"
            onClick={handleMarkReviewed}
            disabled={loading || saving}
            data-testid="cs-mark-reviewed"
          >
            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Mark reviewed
          </Button>
          {!loading && (
            <>
              <Button
                size="sm" variant="outline"
                onClick={() => draftWithAi("fill_empty")}
                disabled={drafting || saving}
                className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                data-testid="cs-ai-draft"
                title="Draft empty sections using the client's records"
              >
                {drafting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                {drafting ? "Drafting…" : "Draft with AI"}
              </Button>
              {Object.keys(aiProvenance || {}).length > 0 && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => draftWithAi("replace_all")}
                  disabled={drafting || saving}
                  className="text-indigo-700 hover:text-indigo-950"
                  data-testid="cs-ai-redraft"
                  title="Refresh only sections that were previously AI-drafted. Clinician-authored sections are preserved."
                >
                  Refresh AI drafts
                </Button>
              )}
            </>
          )}
          {revisionsCount > 0 && (
            <Button
              size="sm" variant="ghost"
              onClick={() => setShowHistory(true)}
              data-testid="cs-history-open"
              className="text-zinc-600 hover:text-zinc-950"
            >
              <History className="h-3.5 w-3.5 mr-1" />
              History ({revisionsCount})
            </Button>
          )}
        </div>
      </div>

      {showHistory && (
        <SummaryHistoryDialog
          email={email}
          canonicalId={canonicalId}
          onClose={() => setShowHistory(false)}
        />
      )}
    </Card>
  );
}


function SummaryHistoryDialog({ email, canonicalId, onClose }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${encodeURIComponent(email)}/clinical-summary/history`, {
        params: canonicalId ? { canonical_client_id: canonicalId } : undefined,
      })
      .then((r) => { if (!cancelled) setRevisions(r.data?.revisions || []); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email, canonicalId]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
                     data-testid="cs-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Clinical Summary — revision history
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto space-y-3 pr-1">
          {loading && (
            <div className="text-xs text-zinc-500 flex items-center gap-2 py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          )}
          {!loading && revisions.length === 0 && (
            <div className="text-xs text-zinc-500 py-4 text-center">
              No prior revisions.
            </div>
          )}
          {revisions.map((rev, i) => {
            const sec = rev?.sections || {};
            const anySection = SECTIONS.some((s) => sec[s.key]?.trim());
            return (
              <div key={i} className="rounded-md border border-zinc-200 p-3 space-y-1.5"
                   data-testid={`cs-history-row-${i}`}>
                <div className="text-2xs text-zinc-500 flex items-center justify-between">
                  <span>Edited by <b className="text-zinc-700">{rev?.by?.name || "—"}</b></span>
                  <span>{fmtWhen(rev?.at)}</span>
                </div>
                {anySection ? (
                  <div className="space-y-1.5">
                    {SECTIONS.map((s) => (sec[s.key] || "").trim() && (
                      <div key={s.key}>
                        <div className="text-2xs uppercase tracking-wide text-zinc-500">
                          {s.label}
                        </div>
                        <pre className="text-xs bg-zinc-50 border border-zinc-100 rounded p-2 whitespace-pre-wrap font-sans text-zinc-800">
                          {sec[s.key]}
                        </pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre className="text-xs bg-zinc-50 border border-zinc-100 rounded p-2 whitespace-pre-wrap font-sans text-zinc-800">
                    {rev?.text || <em className="text-zinc-400">(empty)</em>}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
